/*
 * Copyright (c) 2016-2023 VMware, Inc. All Rights Reserved.
 * This software is released under MIT license.
 * The full license information can be found in LICENSE in the root directory of this project.
 */
import { focusElement, initializeKeyListItems, setActiveKeyListItem } from '../utils/focus.js';
import { validKeyNavigationCode } from '../utils/keycodes.js';
import { getFlattenedFocusableItems } from '../utils/traversal.js';
import { getNextKeyListItem } from './key-navigation.utils.js';
/**
 * Provides key list naviation behavior
 * https://webaim.org/techniques/keyboard/
 */
export function keyNavigationList(config = {}) {
    return (target) => {
        target.addInitializer((instance) => {
            if (!instance.keyNavigationListController) {
                instance.keyNavigationListController = new KeyNavigationListController(instance, config);
            }
        });
    };
}
export class KeyNavigationListController {
    constructor(host, config = {}) {
        this.host = host;
        this.host.addController(this);
        this.config = {
            keyListItems: 'keyListItems',
            layout: 'horizontal',
            manageFocus: true,
            manageTabindex: true,
            loop: false,
            dir: this.host.getAttribute('rtl'),
            ...config,
        };
    }
    get listItems() {
        return this.host[this.config.keyListItems];
    }
    async hostConnected() {
        await this.host.updateComplete;
        this.initializeTabIndex();
        this.host.addEventListener('click', (e) => this.clickItem(e));
        this.host.addEventListener('keydown', (e) => this.focusItem(e));
        this.host.shadowRoot?.addEventListener('click', (e) => this.clickItem(e));
        this.host.shadowRoot?.addEventListener('keydown', (e) => this.focusItem(e));
    }
    initializeTabIndex() {
        if (this.config.manageFocus && this.config.manageTabindex) {
            initializeKeyListItems(this.listItems);
        }
    }
    clickItem(e) {
        const activeItem = this.getActiveItem(e);
        if (activeItem) {
            this.setActiveItem(e, activeItem);
        }
    }
    focusItem(e) {
        if (validKeyNavigationCode(e)) {
            const activeItem = this.getActiveItem(e);
            if (activeItem) {
                const { next, previous } = getNextKeyListItem(activeItem, Array.from(this.listItems), {
                    ...this.config,
                    code: e.code,
                });
                if (next !== previous) {
                    // todo: test for only emiting when position has changed
                    this.setActiveItem(e, this.listItems[next], this.listItems[previous]);
                }
            }
        }
    }
    getActiveItem(e) {
        return Array.from(this.listItems).find(c => c === e.target.closest(this.listItems[0].tagName.toLocaleLowerCase()) ?? c === e.target);
    }
    setActiveItem(e, activeItem, previousItem) {
        if (this.config.manageFocus) {
            if (this.config.manageTabindex) {
                setActiveKeyListItem(this.listItems, activeItem);
            }
            const items = getFlattenedFocusableItems(activeItem);
            const item = items[0] ?? activeItem;
            focusElement(item);
            e.preventDefault();
        }
        activeItem.dispatchEvent(new CustomEvent('cdsKeyChange', {
            bubbles: true,
            detail: {
                activeItem,
                previousItem,
                code: e.code,
                metaKey: e.ctrlKey || e.metaKey,
                keyListItems: this.config.keyListItems,
            },
        }));
    }
}
